import React, { useState, useEffect } from 'react';
import Toggle from '../../../Common/Toggle/Toggle';
import { FaPlus, FaSearch, FaEdit, FaTrash, FaSave, FaTimes } from 'react-icons/fa';
import '../Nucleos/Nucleos.css';

import API_BASE_URL from '../../../../config/api';

const Departamentos = () => {
  const [departamentos, setDepartamentos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingDepartamento, setEditingDepartamento] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    departamento: '',
    sigla: '',
    responsavel: '',
    ativo: true
  });

  const carregarDepartamentos = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/departamentos`);
      if (!response.ok) {
        throw new Error('Erro ao buscar departamentos');
      }

      const data = await response.json();

      const formatados = data.map((item) => ({
        id: item.id_departamento,
        departamento: item.nome_departamento,
        sigla: item.sigla,
        responsavel: item.responsavel,
        ativo: item.ativo
      }));

      setDepartamentos(formatados);
    } catch (error) {
      console.error('Erro ao carregar departamentos:', error);
      alert('Erro ao carregar departamentos. Verifique a conexão com o servidor.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDepartamentos();
  }, []);

  const departamentosFiltrados = departamentos.filter((departamento) =>
    (departamento.departamento || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        nome_departamento: formData.departamento,
        sigla: formData.sigla,
        responsavel: formData.responsavel,
        ativo: formData.ativo
      };

      if (editingDepartamento) {
        const response = await fetch(
          `${API_BASE_URL}/departamentos/${editingDepartamento.id}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          }
        );

        if (!response.ok) {
          throw new Error('Erro ao atualizar departamento');
        }

        const atualizado = await response.json();

        setDepartamentos((prev) =>
          prev.map((departamento) =>
            departamento.id === editingDepartamento.id
              ? {
                  ...departamento,
                  departamento: atualizado.nome_departamento,
                  sigla: atualizado.sigla,
                  responsavel: atualizado.responsavel,
                  ativo: atualizado.ativo
                }
              : departamento
          )
        );
      } else {
        const response = await fetch(`${API_BASE_URL}/departamentos`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          throw new Error('Erro ao criar departamento');
        }

        const criado = await response.json();

        const novoDepartamento = {
          id: criado.id_departamento,
          departamento: criado.nome_departamento,
          sigla: criado.sigla,
          responsavel: criado.responsavel,
          ativo: criado.ativo
        };

        setDepartamentos((prev) => [...prev, novoDepartamento]);
      }

      resetForm();
    } catch (error) {
      console.error('Erro ao salvar departamento:', error);
      alert('Erro ao salvar departamento. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (departamento) => {
    setEditingDepartamento(departamento);
    setFormData({
      departamento: departamento.departamento,
      sigla: departamento.sigla || '',
      responsavel: departamento.responsavel || '',
      ativo: departamento.ativo
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este departamento?')) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/departamentos/${id}`,
        {
          method: 'DELETE'
        }
      );

      if (!response.ok && response.status !== 204) {
        throw new Error('Erro ao excluir departamento');
      }

      setDepartamentos((prev) =>
        prev.filter((departamento) => departamento.id !== id)
      );
    } catch (error) {
      console.error('Erro ao excluir departamento:', error);
      alert('Erro ao excluir departamento. Tente novamente.');
    }
  };

  const resetForm = () => {
    setFormData({
      departamento: '',
      sigla: '',
      responsavel: '',
      ativo: true
    });
    setEditingDepartamento(null);
    setShowForm(false);
  };

  return (
    <div className="nucleos-container">
      {!showForm ? (
        <>
          <div className="tabela-header">
            <div className="header-content">
              <h1>Departamentos</h1>
            </div>
            <button 
              className="btn-primary"
              onClick={() => setShowForm(true)}
            >
              <FaPlus /> Nova Departamento
            </button>
          </div>

          <div className="table-container">
            <div className="table-controls">
              <div className="search-container">
                <FaSearch />
                <input
                  type="text"
                  placeholder="Pesquisar departamentos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="table-info">
                <span>
                  {loading
                    ? 'Carregando departamentos...'
                    : `${departamentosFiltrados.length} registro(s) encontrado(s)`}
                </span>
              </div>
            </div>

            <div className="table-wrapper">
              <table className="data-table nucleos-table">
                <thead>
                  <tr>
                    <th className="col-id">ID</th>
                    <th className="col-nome">Departamento</th>
                    <th className="col-sigla">Sigla</th>
                    <th className="col-nome">Responsável</th>
                    <th className="col-status">Status</th>
                    <th className="col-acoes">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                        Carregando...
                      </td>
                    </tr>
                  ) : departamentosFiltrados.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                        Nenhum registro encontrado
                      </td>
                    </tr>
                  ) : (
                    departamentosFiltrados.map(departamento => (
                      <tr key={departamento.id}>
                        <td>{departamento.id}</td>
                        <td>{departamento.departamento}</td>
                        <td>{departamento.sigla}</td>
                        <td>{departamento.responsavel}</td>
                        <td>
                          <span className={`status-badge ${departamento.ativo ? 'ativo' : 'inativo'}`}>
                            {departamento.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="col-acoes">
                          <div className="action-buttons">
                            <button 
                              className="btn-edit"
                              onClick={() => handleEdit(departamento)}
                              title="Editar"
                            >
                              <FaEdit />
                            </button>
                            <button 
                              className="btn-delete"
                              onClick={() => handleDelete(departamento.id)}
                              title="Excluir"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="form-container inline-form">
          <div className="form-header">
            <h2>{editingDepartamento ? 'Editar Departamento' : 'Cadastrar Departamento'}</h2>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="form-content">
              <div className="form-group">
                <label htmlFor="departamento">Nome do Departamento *</label>
                <input
                  type="text"
                  id="departamento"
                  className="form-control"
                  value={formData.departamento}
                  onChange={(e) => setFormData({ ...formData, departamento: e.target.value })}
                  required
                  maxLength={100}
                  placeholder="Digite o nome do departamento"
                />
              </div>

              <div className="form-group">
                <label htmlFor="sigla">Sigla</label>
                <input
                  type="text"
                  id="sigla"
                  className="form-control"
                  value={formData.sigla}
                  onChange={(e) => setFormData({ ...formData, sigla: e.target.value })}
                  maxLength={20}
                  placeholder="Ex: RH"
                />
              </div>

              <div className="form-group">
                <label htmlFor="responsavel">Responsável</label>
                <input
                  type="text"
                  id="responsavel"
                  className="form-control"
                  value={formData.responsavel}
                  onChange={(e) => setFormData({ ...formData, responsavel: e.target.value })}
                  maxLength={100}
                  placeholder="Digite o nome do responsável"
                />
              </div>

              <div className="form-group">
                <Toggle
                  id="ativo"
                  label="Status:"
                  checked={formData.ativo}
                  onChange={(e) => setFormData({...formData, ativo: e.target.checked})}
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={resetForm}>
                <FaTimes /> Cancelar
              </button>
              <button type="submit" className="btn-primary" disabled={saving}>
                <FaSave /> {editingDepartamento ? 'Atualizar' : 'Cadastrar'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Departamentos;
